import { Component, Input, OnChanges, TemplateRef, ViewChild } from "@angular/core";
import { VTableComponent } from "@app/directives/_layout/v-table/_components/v-table.component";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IVehicleInput } from "@interfaces/IVehicleInput";
import { DateService, DateUnit } from "@services/date.service";
import { TranslationService } from "@services/translation.service";
import { IVehicleInputRecordHistory, VehicleInputService } from "@services/vehicleInput.service";
import './input-list.component.scss';

interface IInputDataItem {
    Id: number;
    name: string;
    startTime: Date;
    startLocation: number;
    endTime: Date;
    endLocation: number;
    duration: number;
}

@Component({
    selector: 'input-list',
    template: require('./input-list.component.html')
})
export class InputListComponent implements OnChanges {
    @Input() vehicleId: number;
    @Input() vehicleReg: string;
    @Input() start: Date;
    @Input() end: Date;

    @ViewChild('stackedDateLocationCell') stackedDateLocationCellTemplate: TemplateRef<any>;
    @ViewChild('inputListTable') table: VTableComponent;
    //--
    loading = false;

    inputsData: IInputDataItem[];
    vehicleInputs: IVehicleInput[];
    inputHistory: IVehicleInputRecordHistory[];

    //// #Tables
    tableColumns: IVTableColumn<IInputDataItem>[];
    tableRows: IVTableRow[];

    constructor(
        private readonly dateService: DateService,
        private readonly translationService: TranslationService,
        private readonly vehicleInputService: VehicleInputService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
        this.createTable();
    }

    ngOnChanges(): void {
        this.getData();
    }

    getData(): Promise<void> {
        this.loading = true;
        const promises = [];

        promises.push(this.vehicleInputService.getVehicleInputs(this.vehicleId));
        promises.push(this.vehicleInputService.getVehicleInputHistory(this.vehicleId, this.start, this.end));

        return Promise.all(promises).then(([vehicleInputs, inputHistory]) => {
            this.vehicleInputs = vehicleInputs;
            this.inputHistory = inputHistory;
        }).finally(() => {
            this.setupInputsTable();
            this.loading = false;
        })
    }

    createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: 'VehicleTimeline.ActiveInputs', width: 200, sorted: true }),
                createCellFunction: (item) => this.vTableService.createTableCell(item.name)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'VehicleTimeline.InputTableHeaderStartTime', width: 200 }),
                createCellFunction: (item) => this.vTableService.createTableCell({ date: item.startTime, plotId: item.startLocation }, { exportData: this.dateService.formatDate(item.startTime), sortData: item.startTime, template: this.stackedDateLocationCellTemplate })
            },
            {

                header: this.vTableService.createDateTableHeader({ tKey: 'VehicleTimeline.InputTableHeaderEndTime', width: 200 }),
                createCellFunction: (item) => this.vTableService.createTableCell(
                    {
                        date: item.endTime && new Date(item.endTime),
                        plotId: item.endLocation
                    },
                    {
                        exportData: item.endTime ? this.dateService.formatDate(item.endTime) : this.translationService.t("VehicleTimeline.Current"),
                        sortData: item.endTime,
                        template: this.stackedDateLocationCellTemplate
                    })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Duration' }),
                createCellFunction: (item) => this.vTableService.createDurationCellFromDates(item.startTime, this.getEndTime(item))
            }
        ];
    }


    private getEndTime(item: IInputDataItem): Date {
        if (item.endTime) {
            return new Date(item.endTime);
        }

        return new Date();
    }

    private setupInputsTable(): void {
        this.inputsData = [];

        if (!this.inputHistory) {
            return;
        }

        this.inputHistory.forEach((input, index) => {

            this.vehicleInputs.forEach(vehicleInput => {
                if (input.InputId == vehicleInput.Id) {
                    input.inputName = vehicleInput.name;
                    input.inputStateDescription = vehicleInput.InputStateDescription;
                }
            });

            this.inputsData.push(
                this.createInputItem(input, index),
            );
        });
        this.tableRows = this.vTableService.createTableRows(this.inputsData, this.tableColumns);
    }

    private createInputItem(input: IVehicleInputRecordHistory, index: number): IInputDataItem {
        let startDate = new Date(input.StartUtc);
        let endDate = input.EndUtc ? new Date(input.EndUtc) : new Date();

        return {
            Id: index,
            name: input.inputName,
            startTime: startDate,
            startLocation: input.StartPlotId,
            endTime: input.EndUtc ? new Date(input.EndUtc) : null,
            endLocation: input.EndPlotId,
            duration: endDate.getTime() - startDate.getTime()
        }

    }
}
